import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Home as HomeIcon, QrCode, PiggyBank, Shirt, Lock, ArrowLeft } from 'lucide-react';
import MuestrarioPrecios from '../components/MuestrarioPrecios';
import MyQR from '../components/MyQR';
import MiAhorro from '../components/MiAhorro';
import { VD, GRAD, SOMBRA } from '../lib/brand';
import { benefitsService } from '../services/benefits';
import { useReferido } from '../hooks/useReferido';
import { PlanSwipe } from '../components/PlanSwipe';
import { FlipCard } from '../components/ui/FlipCard';

/**
 * `LOGIN_CANDADOS` — el perfil que se abre al tocar «Paga menos».
 *
 * **Esta pantalla es pública a propósito.** No pide cuenta, no pide correo, no
 * pide nada: el visitante entra y ve de golpe lo que hay dentro, con todas las
 * tarjetas bajo candado menos la promesa de una gratis. La cuenta se pide
 * después, cuando ya eligió cuál quiere — que es cuando dar sus datos tiene
 * sentido para él y no solo para nosotros.
 *
 * Dos caminos desde aquí, como en el mockup:
 *   - «Activar mi beneficio» → elegir cuál (paso 3)
 *   - el bloque de precio → pagar de una vez
 */
export default function Bienvenida() {
  const navigate = useNavigate();
  const [beneficios, setBeneficios] = useState([]);
  /*
    El beneficio de bienvenida solo se ofrece a quien llega por un referido
    (Iván, 30-ago-2026). Sin código, esta pantalla abre igual —el catálogo, el
    muestrario, las pestañas— pero sin la tarjeta del gratis, y las tarjetas
    bloqueadas llevan al registro en vez de a elegir cuál desbloquear.
  */
  const referido = useReferido();
  const destinoBloqueadas = referido === true ? '/beneficios/elegir' : '/subscription';
  /*
    El visitante ve la app entera, no solo una pantalla.

    Iván: «ahí también me tiene que aparecer para que pueda ir a las demás
    secciones, aunque algunas estén bloqueadas, que me aparezcan». Enseñar la
    barra con candados es más honesto que esconderla: dice qué hay dentro y
    cuánto falta para tenerlo, en vez de dejar creer que esto es todo.

    El muestrario **sí se abre** —es público y es el mejor argumento que hay
    antes de registrarse—; el QR y el ahorro llevan a elegir el beneficio, que
    es el paso que los desbloquea.
  */
  const [pestana, setPestana] = useState(() => {
    /* `?ver=qr` abre directo esa sección. Sirve para enlazar a una pantalla
       concreta —y para poder verificarla sin tener que simular el toque. */
    const v = new URLSearchParams(window.location.search).get('ver');
    return ['inicio', 'muestrario', 'qr', 'ahorro'].includes(v) ? v : 'inicio';
  });

  useEffect(() => {
    let vigente = true;
    benefitsService
      .getAll()
      .then((r) => vigente && setBeneficios(r?.data ?? []))
      .catch(() => {});
    return () => {
      vigente = false;
    };
  }, []);

  /* La pestaña del muestrario reemplaza el contenido, no abre otra ruta: el
     visitante sigue donde estaba y vuelve con un toque. */
  /*
    El visitante entra y mira.

    Las cuatro secciones se abren de verdad; lo que está apagado son las
    funciones. Antes había candados que rebotaban a otra pantalla, y eso no
    enseña nada: quien tocaba «QR» quería ver el QR, no volver al catálogo.

    `MiAhorro` no necesita nada especial — sin sesión cae sola en su versión
    tenue con candados, que es exactamente lo que hay que enseñar.
  */
  if (pestana !== 'inicio') {
    return (
      <div style={{ minHeight: '100vh', background: '#fff', paddingBottom: 110 }}>
        {/* Dentro de las pestañas, «atrás» es volver al inicio de esta misma
            pantalla, no salir de la app: es donde espera llegar quien entró a
            mirar una sección. */}
        <BotonAtras onClick={() => setPestana('inicio')} />
        {pestana === 'muestrario' && <MuestrarioPrecios />}
        {pestana === 'qr' && <MyQR soloVista />}
        {pestana === 'ahorro' && <MiAhorro />}
        <BarraVisitante actual={pestana} onCambiar={setPestana} navigate={navigate} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#fff', paddingBottom: 110 }}>
      {/*
        La salida.

        Quien toca «¡Paga menos!» en el landing aterriza aquí y se quedaba
        atrapado: ni botón de volver ni forma de regresar a la portada salvo el
        gesto del navegador —que en un teléfono mucha gente no usa—. Ivan lo
        pidió el 13-ago-2026: arriba a la izquierda y discreto.

        Discreto de verdad: sin fondo, sin borde, solo la flecha en gris. Esta
        pantalla vende, y un botón de salida con peso visual compite con el
        beneficio gratis que es lo que la persona vino a ver.
      */}
      <BotonAtras onClick={() => navigate('/')} />

      <div style={{ padding: 'max(58px, env(safe-area-inset-top, 58px)) 24px 0' }}>
        {/*
          Aquí iba el «BIENVENIDO !» a toda página. Lo quitó Iván (13-ago-2026):
          ocupaba la mejor parte de la pantalla para saludar, y lo que la persona
          necesita ver de entrada es que tiene un beneficio gratis esperándola.
          La tarjeta roja empieza ahora arriba del todo.
        */}

        {/*
          Camino 1 — el beneficio de bienvenida.

          El botón va DENTRO de la tarjeta, no montado sobre su borde inferior.
          Antes se superponía con `marginTop: -14` y Iván lo describió como «toda
          encimada»: se leía como dos piezas peleándose, no como una tarjeta con
          su acción. En `LOGIN_CANDADOS` la tarjeta es una sola cosa —icono,
          promesa y botón— y el aire está adentro.
        */}
        {referido === true && (
        <div
          style={{
            background: 'linear-gradient(100deg, #F73E48, #FA759F)',
            borderRadius: 26,
            padding: '24px 22px 22px',
            boxShadow: '0 16px 36px -18px rgba(247,62,72,0.7)',
          }}
        >
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <Gift size={54} color="#fff" strokeWidth={1.6} style={{ flex: 'none', opacity: 0.95 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 17, color: '#fff', lineHeight: 1.3 }}>
                Tienes un beneficio{' '}
                <strong className="font-display" style={{ fontSize: 21, fontWeight: 700 }}>
                  GRATIS
                </strong>{' '}
                de <strong style={{ fontWeight: 700 }}>bienvenida</strong>
              </div>
              <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.9)', lineHeight: 1.4, marginTop: 6 }}>
                Usa cualquiera de los beneficios exclusivos de vondi
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate('/beneficios/elegir')}
            style={{
              width: '100%',
              height: 52,
              marginTop: 20,
              borderRadius: 9999,
              background: '#fff',
              border: 'none',
              color: '#F73E48',
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: '0.01em',
              cursor: 'pointer',
              boxShadow: '0 8px 20px -10px rgba(14,14,16,0.35)',
            }}
          >
            ACTIVAR MI BENEFICIO!
          </button>
        </div>
        )}

        {/* Camino 2 — pagar de una vez. Sin referido es el único camino, así
            que sube al lugar que ocupaba la tarjeta del gratis. */}
        <div style={{ marginTop: referido === true ? 22 : 0 }}>
          {/* Directo al checkout, que ya crea la cuenta: sin pantalla de
              registro de por medio. Se lleva el plan que eligió. */}
          <PlanSwipe planSugerido="annual" onElegir={(plan) => navigate('/subscription', { state: { plan: plan.key } })} />
        </div>
      </div>

      {/* Lo que hay dentro, bajo candado.
          Iba encabezado por un rótulo «Beneficios exclusivos»; lo quitó Iván
          (13-ago-2026). Las tarjetas ya dicen lo que son. */}
      <div style={{ padding: '26px 24px 0' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {beneficios.map((b) => (
            <FlipCard
              key={b.id}
              benefit={b}
              estado="bloqueada"
              onClick={() => navigate(destinoBloqueadas)}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => navigate('/login')}
        style={{
          display: 'block',
          margin: '28px auto 0',
          background: 'transparent',
          border: 'none',
          color: '#6E6E77',
          fontSize: 13.5,
          fontWeight: 600,
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        ¿Ya eres miembro? Acceder
      </button>

      <BarraVisitante actual={pestana} onCambiar={setPestana} navigate={navigate} />
    </div>
  );
}

/* La barra del visitante: las mismas cuatro secciones de la app, con candado
   en las que todavía no tiene. */
/**
 * La flecha de volver, arriba a la izquierda.
 *
 * Va en posición absoluta y por encima del contenido para no empujar nada de la
 * maqueta: la pantalla ya estaba compuesta y meter la flecha en el flujo
 * desplazaría la tarjeta del beneficio hacia abajo.
 *
 * El área que responde al toque es de 44px —el mínimo cómodo en un teléfono—
 * aunque la flecha dibujada sea más pequeña. Un botón sutil no tiene por qué
 * ser un botón difícil de atinar.
 */
function BotonAtras({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Volver"
      style={{
        position: 'absolute',
        top: 'max(16px, env(safe-area-inset-top, 16px))',
        left: 14,
        zIndex: 20,
        width: 44,
        height: 44,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: VD.pizarra,
      }}
    >
      <ArrowLeft size={22} strokeWidth={2.2} />
    </button>
  );
}

function BarraVisitante({ actual, onCambiar, navigate }) {
  /* Todas se abren. El candado ya no bloquea: avisa que ahí dentro las
     funciones están apagadas hasta activar el beneficio. */
  const pestanas = [
    { id: 'inicio', icon: HomeIcon, conCandado: false },
    { id: 'muestrario', icon: Shirt, conCandado: false },
    { id: 'qr', icon: QrCode, conCandado: true },
    { id: 'ahorro', icon: PiggyBank, conCandado: true },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '0 20px max(24px, env(safe-area-inset-bottom, 24px))',
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-evenly',
          gap: 4,
          width: '100%',
          maxWidth: 380,
          background: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderRadius: 9999,
          padding: '6px 10px',
          boxShadow: SOMBRA.md,
          pointerEvents: 'all',
        }}
      >
        {pestanas.map(({ id, icon: Icono, conCandado }) => {
          const activa = actual === id;
          return (
            <button
              key={id}
              onClick={() => onCambiar(id)}
              aria-label={id}
              style={{
                position: 'relative',
                flex: '1 1 0',
                minWidth: 0,
                maxWidth: 60,
                height: 50,
                borderRadius: 9999,
                background: activa ? GRAD.vivo : 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background .18s ease',
              }}
            >
              <Icono size={20} color={activa ? '#fff' : VD.pizarra} />
              {conCandado && (
                <Lock
                  size={11}
                  color={VD.pizarra}
                  style={{ position: 'absolute', top: 9, right: 10 }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
